import { type TFunction } from 'next-i18next'

import { type TradeInFormFields } from '@/components/vip/vip-trade-in/constants/vipTradeIn'

type BuildTradeInMessageParameters = {
  formValues?: Partial<TradeInFormFields>
  listingId: string
  listingUrl: string
  t: TFunction
}

/**
 * Constructs a localized trade-in estimate message based on form input and listing details.
 *
 * The message includes contact information and trade-in vehicle details,
 * formatted using translation keys from the `trade_in` namespace.
 *
 * @param formValues - The user's submitted trade-in form values.
 * @param listingId - The ID of the listing the trade-in is for.
 * @param listingUrl - The full URL of the listing.
 * @param t - The i18next translation function.
 *
 * @returns {string} A fully formatted message string, or an empty string if form values are missing.
 */
export const buildTradeInMessage = ({
  formValues,
  listingId,
  listingUrl,
  t,
}: BuildTradeInMessageParameters) => {
  if (!formValues) return ''

  const { firstName, lastName, email, phone, year, make, model, mileage, vehicleType } = formValues

  let message = `<p>${t('trade_in:message.subject', { listingId })}</p>`

  message += `<p>${t('trade_in:message.intro', {
    firstName,
    lastName,
    listingUrl,
  })}</p>`

  message += `<p>${t('trade_in:message.contact.information.title')}<br/>`
  message += `${t('trade_in:name.label')}: ${firstName} ${lastName}<br/>`
  message += `${t('trade_in:phone_number.label')}: ${phone}<br/>`
  message += `${t('trade_in:email.label')}: ${email}</p>`

  message += `<p>${t('trade_in:vehicle.information.title')}<br/>`

  if (vehicleType) {
    message += `${t('trade_in:vehicle.type.label')}: ${vehicleType}<br/>`
  }

  message += `${t('trade_in:vehicle.year.label')}: ${year}<br/>`
  message += `${t('trade_in:vehicle.make.label')}: ${make}<br/>`

  if (model) {
    message += `${t('trade_in:vehicle.model.label')}: ${model}<br/>`
  }

  if (mileage) {
    message += `${t('trade_in:vehicle.mileage.label')}: ${mileage}</p>`
  }

  return message
}
